import type { AssetMatte, OverlayClip, VideoClip } from "@cva/shared";
import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Loader2, PersonStanding, RefreshCw, Type, X } from "lucide-react";
import { Progress } from "../../components/ui/card";
import { DropdownCheckItem } from "../../components/ui/dropdown";
import { estimateProgress, formatRemaining, useElapsed } from "../../lib/activity";
import { useEnv } from "../env";
import { useEditor, useEditorApi } from "../store";
import { Section, Segmented } from "../ui";
import {
  addTitleBehindPerson,
  type ClipRef,
  canCutOutPerson,
  cutOutPerson,
  hasPersonCutout,
  isBehindCutout,
  setBehindCutout,
  setClipCutout,
} from "./person-cutout";

/**
 * "Person freistellen" in the clip properties: starts it, shows the progress
 * and then offers the background choice and a title behind the person.
 */
export function PersonCutoutSection({
  clip,
  clipRef,
}: {
  clip: VideoClip | OverlayClip;
  clipRef: ClipRef;
}) {
  const env = useEnv();
  const store = useEditorApi();
  const queryClient = useQueryClient();
  const readOnly = useEditor((s) => s.readOnly);
  if (!canCutOutPerson(clip)) return null;
  const asset = env.assets.get(clip.assetId);
  const matte = asset?.matte ?? null;
  const start = () =>
    void cutOutPerson({ orgId: env.orgId, store, queryClient, ref: clipRef, asset });

  if (!clip.cutout) {
    return (
      <Section title="Person freistellen">
        <p className="text-xs text-zinc-500">
          Die Person wird zur eigenen Ebene: Texte lassen sich zwischen Hintergrund und Person
          legen, oder der Hintergrund verschwindet ganz.
        </p>
        <button
          type="button"
          disabled={readOnly || !asset}
          onClick={start}
          className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-sm font-medium text-zinc-700 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800 disabled:opacity-50 [&_svg]:size-4"
        >
          <PersonStanding /> Person freistellen
        </button>
        {matte?.status === "ready" ? (
          <p className="text-[11px] text-zinc-500">Dieses Video ist schon freigestellt.</p>
        ) : null}
      </Section>
    );
  }

  const background = clip.cutout.background;
  return (
    <Section
      title="Person freigestellt"
      actions={
        readOnly ? null : (
          <button
            type="button"
            onClick={() => setClipCutout(store, clipRef, null)}
            className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800"
            title="Den Clip wieder ohne eigene Personen-Ebene zeigen"
          >
            <X className="size-3.5" /> Aus
          </button>
        )
      }
    >
      <MatteStatus matte={matte} onRetry={readOnly ? undefined : start} />
      <Segmented
        className="w-full"
        value={background}
        onChange={(value) =>
          !readOnly && setClipCutout(store, clipRef, { ...clip.cutout, background: value })
        }
        options={[
          {
            value: "keep",
            label: "Mit Hintergrund",
            title: "Das ganze Bild bleibt, die Person liegt zusätzlich als Ebene darüber",
          },
          {
            value: "remove",
            label: "Nur Person",
            title:
              clipRef.track === "video"
                ? "Nur die Person – dahinter die Seitenfarbe (oder der unscharfe Hintergrund)"
                : "Nur die Person – dahinter die Spuren darunter",
          },
        ]}
      />
      <button
        type="button"
        disabled={readOnly}
        onClick={() => addTitleBehindPerson(store, clipRef)}
        className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 [&_svg]:size-4"
      >
        <Type /> Titel hinter die Person setzen
      </button>
      <p className="text-[11px] text-zinc-500">
        Andere Texte, Formen oder Bilder: Rechtsklick › „Hinter die Person“.
      </p>
    </Section>
  );
}

/** Progress while the server cuts the person out; a failure with a retry. */
export function MatteStatus({
  matte,
  onRetry,
}: {
  matte: AssetMatte | null;
  onRetry?: () => void;
}) {
  const elapsed = useElapsed(matte?.status === "processing" ? matte.elapsedMs : null);
  if (matte?.status === "ready") return null;
  if (matte?.status === "failed") {
    return (
      <div className="rounded-md border border-red-100 bg-red-50 px-2.5 py-2 text-xs text-red-800">
        <p className="flex items-start gap-1.5">
          <CircleAlert className="mt-0.5 size-3.5 shrink-0" />
          {matte.error ?? "Das Freistellen ist fehlgeschlagen."}
        </p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1.5 inline-flex items-center gap-1 font-medium hover:underline"
          >
            <RefreshCw className="size-3.5" /> Erneut versuchen
          </button>
        ) : null}
      </div>
    );
  }
  const started = matte?.elapsedMs !== null && matte?.elapsedMs !== undefined;
  const estimate = estimateProgress(matte?.progress ?? 0, elapsed, matte?.estimatedMs ?? null);
  return (
    <div className="rounded-md border border-brand-100 bg-brand-50/60 px-2.5 py-2">
      <p className="flex items-center gap-1.5 text-xs font-medium text-brand-900">
        <Loader2 className="size-3.5 animate-spin" />
        {started ? "Person wird freigestellt …" : "Freistellen startet …"}
        {started && estimate.remainingMs !== null ? (
          <span className="ml-auto font-normal text-brand-700">
            {formatRemaining(Math.max(0, estimate.remainingMs))}
          </span>
        ) : null}
      </p>
      <Progress className="mt-1.5 h-1.5" value={started ? estimate.progress : 2} />
      <p className="mt-1 text-[11px] text-zinc-500">
        Du kannst weiterarbeiten – bis dahin läuft der Clip wie bisher. Jedes Bild des Videos wird
        einmal freigestellt, danach geht es für alle Clips daraus sofort.
      </p>
    </div>
  );
}

/**
 * Context menus (canvas, timeline): puts elements or clips of upper tracks
 * behind the cut-out person, or in front of it again. Only in videos with one.
 */
export function BehindPersonItem({ ids, onDone }: { ids: readonly string[]; onDone?: () => void }) {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  if (doc.kind !== "video") return null;
  const page = doc.pages[0];
  const targets = ids.filter((id) => {
    const clip = doc.timeline?.overlays?.find((item) => item.id === id);
    if (clip) return !clip.cutout;
    return page?.children.includes(id) && doc.elements[id]?.type !== "captions";
  });
  if (targets.length === 0 || !hasPersonCutout(doc)) return null;
  const behind = targets.every((id) => isBehindCutout(doc, id));
  return (
    <DropdownCheckItem
      checked={behind}
      onSelect={() => {
        setBehindCutout(store, targets, !behind);
        onDone?.();
      }}
    >
      Hinter die Person
    </DropdownCheckItem>
  );
}
